import {sendAnalyticsEvent} from '#/analytics/analytics';
import {ComboboxCustomSearchInput} from '#/atoms/search-bar';
import {VerticalSeparator} from '#/atoms/vertical-separator';
import LandIcon from '#/components/icons/land-icon';
import {useLands} from '#/hooks/query/use-lands';
import {useUnlistedLands} from '#/hooks/query/use-unlisted-lands';
import {Combobox, ComboboxList, ComboboxOption, ComboboxPopover} from '@reach/combobox';
import '@reach/combobox/styles.css';
import {Land, UnlistedLand} from '@shareland/shareland-common';
import {FunctionComponent, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useThrottle} from 'react-use';

const HeaderSearchBar: FunctionComponent<{
  onSearch: (land: Land | UnlistedLand) => void;
  onBlur?: () => void;
  className?: string;
  autoFocus?: boolean;
  isMobile?: boolean;
}> = ({onSearch, onBlur, className, autoFocus = false, isMobile = false}) => {
  const {t} = useTranslation('translation');

  // External hooks
  const {data: lands = []} = useLands();
  const {data: unlistedLands = []} = useUnlistedLands();

  // Internal hooks
  const [term, setTerm] = useState('');
  const results = useLandsMatch(lands, unlistedLands, term);

  // Functions
  const onSelectLand = (value: string) => {
    const land = lands?.find(land => landToDisplay(land) === value);
    const unlistedLand = unlistedLands?.find(land => landToDisplay(land) === value);
    const selectedLand = land ?? unlistedLand;
    if (!selectedLand) return;
    setTerm(landToDisplay(selectedLand));
    onBlur && onBlur();
    onSearch(selectedLand);
    sendAnalyticsEvent.search({search_term: value});
  };

  return (
    <Combobox
      aria-label='Land'
      onSelect={onSelectLand}
      className={`${isMobile ? 'grow' : 'h-full'} relative ${className}`}
      onBlur={onBlur}
    >
      <ComboboxCustomSearchInput
        autoFocus={autoFocus}
        placeholder={t('components.header.searchBar.placeholder')}
        value={term}
        onChange={setTerm}
        variant='light'
        isMobile={isMobile}
      />

      {results && (results?.lands.length > 0 || results?.unlistedLands.length > 0) && (
        <ComboboxPopover
          portal={false}
          className='w-full absolute top-full mt-2 border-0 bg-gray-800 rounded-lg shadow-xl shadow-black z-20 overflow-hidden-clip-path'
        >
          {results.lands.length > 0 && <div className='mt-2' />}

          <ComboboxList className='relative text-xl text-white'>
            {results.lands.map((result, index) => (
              <ComboboxSearchItem key={`land-search-item-${index}`} land={result} />
            ))}

            {results.lands.length > 0 && results?.unlistedLands.length > 0 && <VerticalSeparator />}

            <div className='text-sm text-gray-400 font-semibold mt-0 mb-1 ml-2'>
              {t('components.header.searchBar.result.unlistedSectionTitle')}
            </div>

            {results.unlistedLands.map((result, index) => (
              <ComboboxSearchItem key={`unlisted-land-search-item-${index}`} land={result} />
            ))}
          </ComboboxList>
        </ComboboxPopover>
      )}
    </Combobox>
  );
};

const ComboboxSearchItem: FunctionComponent<{
  land: Land | UnlistedLand;
}> = ({land}) => {
  return (
    <ComboboxOption
      onMouseDown={e => {
        e.preventDefault();
      }}
      value={landToDisplay(land)}
      className='p-2 px-4 text-sm font-bold hover:bg-gray-500 aria-selected:bg-gray-600 hover:aria-selected:bg-gray-500'
    >
      <div className='flex items-center gap-2.5'>
        <div>
          <LandIcon land={land} />
        </div>
        {landToDisplay(land)}
      </div>
    </ComboboxOption>
  );
};

const findByPartialNameWithMaxResults = <T extends UnlistedLand>(
  lands: T[],
  searchTerm: string,
  maxResults = 10,
): T[] => {
  const results: T[] = [];
  for (const land of lands) {
    if (landToDisplay(land).toLocaleLowerCase().includes(searchTerm) && land.tokenSymbol.length > 0) {
      results.push(land);
    }
    if (results.length >= maxResults) {
      break;
    }
  }
  return results;
};

const landToDisplay = (land: UnlistedLand) =>
  land.type === 'NEIGHBORHOOD' ? `${land.name}, ${land.city}, ${land.state}` : `${land.name}, ${land.state}`;

const findLandsByPartialName = <T extends UnlistedLand>(landsData: T[], searchTerm: string): T[] =>
  findByPartialNameWithMaxResults(landsData, searchTerm)
    .sort((landA, landB) => landA.name.localeCompare(landB.name))
    .sort((landA, landB) => landA.type.localeCompare(landB.type));

const useLandsMatch = (
  lands: Land[],
  unlistedLands: UnlistedLand[],
  searchTerm: string,
): {lands: Land[]; unlistedLands: UnlistedLand[]} | null => {
  const throttledTerm = useThrottle(searchTerm, 100);

  return useMemo(() => {
    const trimmedTerm = throttledTerm.trim().toLowerCase();

    return trimmedTerm === ''
      ? null
      : {
          lands: findLandsByPartialName(lands, trimmedTerm),
          unlistedLands: findLandsByPartialName(
            unlistedLands.sort((land1, land2) => land1.type.localeCompare(land2.type)),
            trimmedTerm,
          ),
        };
  }, [throttledTerm, lands, unlistedLands]);
};

export default HeaderSearchBar;
